/**
 * SubdomainLookupModal — email-based "find your company" flow. Consumes
 * useSubdomainLookup internally and calls the caller-supplied
 * `onLookupSubmit(email)` (which should trigger the server-side email dispatch).
 *
 * Security: no subdomain is ever surfaced in the UI — the server emails it
 * to the address the user enters. The modal only confirms "we sent an email
 * if the account exists", matching the backend's anti-enumeration semantics.
 *
 * @module @puffin-core/login-shell/components/SubdomainLookupModal
 */

import { useRef, useId } from 'react';
import PropTypes from 'prop-types';
import { useSubdomainLookup, ModalState } from '../hooks/useSubdomainLookup.js';
// 🔴 The NARROW subpath, never the barrel. `@puffin-core/page-shell`'s index pulls
// `react-router-dom` (through `useManageAccountAction`), and login-shell is deliberately
// router-free — importing the barrel here dragged the router in and took 21 of this
// package's suites down with `ReferenceError: TextEncoder is not defined`, including ones
// that touch no modal at all. `./modal-chrome` joins the existing narrow exports
// (`./section-pill`, `./honeypot-field`, …) that exist for exactly this reason.
import { Modal } from '@puffin-core/page-shell/modal';
import { FormField } from '@puffin-core/page-shell/form-field';
import { useLoginTranslate } from '../hooks/useLoginTranslate.js';

/**
 * The "we emailed it to you" confirmation. Module-private — extracted so the
 * modal's own return is a single success-vs-form branch.
 */
function SubdomainLookupSuccess({ descId, translate, onClose }) {
  return (
    <>
      <p id={descId} className="modal-subtitle">
        {translate('modal.subdomainLookup.success')}
      </p>
      <div className="modal-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={onClose}
          data-testid="subdomain-lookup-success-close"
        >
          {translate('modal.closeLabel')}
        </button>
      </div>
    </>
  );
}

SubdomainLookupSuccess.propTypes = {
  descId: PropTypes.string.isRequired,
  translate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

/**
 * The email-entry form. Module-private. The retry-vs-cancel secondary button is
 * resolved into a named slot above the return: both arms render a button, so the
 * branch is pure presentation and belongs out of the markup.
 */
function SubdomainLookupForm({
  descId,
  translate,
  email,
  emailInputRef,
  apiError,
  validationError,
  isLoading,
  isError,
  handleEmailChange,
  handleSubmit,
  handleRetry,
  onClose,
}) {
  const secondaryAction = isError ? (
    <button
      type="button"
      className="btn btn-secondary"
      onClick={handleRetry}
      disabled={isLoading}
      data-testid="subdomain-lookup-retry"
    >
      {translate('modal.subdomainLookup.retry')}
    </button>
  ) : (
    <button
      type="button"
      className="btn btn-secondary"
      onClick={onClose}
      disabled={isLoading}
      data-testid="subdomain-lookup-cancel"
    >
      {translate('modal.closeLabel')}
    </button>
  );

  return (
    <>
      <p id={descId} className="modal-subtitle">
        {translate('modal.subdomainLookup.description')}
      </p>
      {apiError && (
        <div className="form-error" role="alert">
          {apiError}
        </div>
      )}
      <form
        onSubmit={handleSubmit}
        noValidate
        aria-label={translate('modal.subdomainLookup.formLabel')}
      >
        <FormField
          id="subdomain-lookup-email"
          label={translate('modal.subdomainLookup.emailLabel')}
          required
          error={validationError}
        >
          <input
            ref={emailInputRef}
            type="email"
            className={`form-input${validationError ? ' form-input-error' : ''}`}
            value={email}
            onChange={handleEmailChange}
            disabled={isLoading}
            autoComplete="email"
            data-testid="subdomain-lookup-email-input"
          />
        </FormField>
        <div className="modal-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
            aria-busy={isLoading ? 'true' : 'false'}
            data-testid="subdomain-lookup-submit"
          >
            {isLoading
              ? translate('modal.subdomainLookup.submitting')
              : translate('modal.subdomainLookup.submit')}
          </button>
          {secondaryAction}
        </div>
      </form>
    </>
  );
}

SubdomainLookupForm.propTypes = {
  descId: PropTypes.string.isRequired,
  translate: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  emailInputRef: PropTypes.object.isRequired,
  apiError: PropTypes.string,
  validationError: PropTypes.string,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  handleEmailChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleRetry: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export function SubdomainLookupModal({ isOpen, onClose, onLookupSubmit, t }) {
  const emailInputRef = useRef(null);

  // MODALCHROME-001 (owner ruling R-9): the ONE chrome, from page-shell. This modal had the whole
  // thing inline — its own Escape branch, its own Tab cycle, and a `setTimeout(0)` autofocus —
  // and it was MISSING the body-scroll lock entirely, so the page behind it scrolled while it was
  // open. The hook supplies all four. Three differences are improvements, not swaps:
  //   - initial focus is SYNCHRONOUS, so it cannot steal focus back from a field the user has
  //     already clicked into (the `setTimeout(0)` it replaces could);
  //   - Escape is a DOCUMENT-level listener, so it works before the user has tabbed in;
  //   - the Tab cycle re-queries focusables on every Tab, so it stays correct across this
  //     modal's three states (form / success / error) instead of going stale at bind time.
  const {
    email,
    modalState,
    validationError,
    apiError,
    isLoading,
    handleEmailChange,
    handleSubmit,
    handleRetry,
  } = useSubdomainLookup({ isOpen, onSubmit: onLookupSubmit });

  const descId = useId();

  // ⚠️ RECIPE §3: `useLoginTranslate` is a CUSTOM hook, so it goes AFTER the
  // computed ones (`web/component-state-order`). Nothing above reads
  // `translate` — `useModalChrome`, `useSubdomainLookup` and the `useId`
  // are all independent of it — and only the JSX below does, so this is the
  // position that satisfies the rule with no TDZ risk.
  const translate = useLoginTranslate(t);

  const isSuccess = modalState === ModalState.SUCCESS;
  const isError = modalState === ModalState.ERROR;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      initialFocusRef={emailInputRef}
      // The heading is CHROME, so it moves out of the two body renderers and into the primitive —
      // which also means ONE `aria-labelledby` wired by `<Modal>` rather than an id threaded
      // through every body. The success view has its own wording, so the title follows the state.
      title={
        isSuccess
          ? translate('modal.subdomainLookup.successTitle')
          : translate('modal.subdomainLookup.title')
      }
      ariaDescribedBy={descId}
      // No header ✕: this modal's only close affordance is in its body actions, and adding one
      // would be a redesign rather than a chrome migration.
      showCloseButton={false}
      testId="subdomain-lookup-modal"
    >
      {isSuccess ? (
        <SubdomainLookupSuccess descId={descId} translate={translate} onClose={onClose} />
      ) : (
        <SubdomainLookupForm
          descId={descId}
          translate={translate}
          email={email}
          emailInputRef={emailInputRef}
          apiError={apiError}
          validationError={validationError}
          isLoading={isLoading}
          isError={isError}
          handleEmailChange={handleEmailChange}
          handleSubmit={handleSubmit}
          handleRetry={handleRetry}
          onClose={onClose}
        />
      )}
    </Modal>
  );
}

SubdomainLookupModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onLookupSubmit: PropTypes.func,
  t: PropTypes.func,
};

export default SubdomainLookupModal;
